import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { extranetAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useSite } from '../../context/SiteContext';

// ── Canvas signature BAT ─────────────────────────────────────
const SignatureCanvas = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [has, setHas] = useState(false);

  const getPos = (e, c) => { const r = c.getBoundingClientRect(), s = e.touches ? e.touches[0] : e; return { x: s.clientX - r.left, y: s.clientY - r.top }; };
  const start  = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); const p = getPos(e, canvasRef.current); ctx.moveTo(p.x, p.y); };
  const draw   = (e) => { e.preventDefault(); if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e, canvasRef.current); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke(); };
  const stop   = () => { drawing.current = false; setHas(true); onChange(canvasRef.current.toDataURL('image/png')); };
  const clear  = () => { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); setHas(false); onChange(null); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ border: '2px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', position: 'relative', touchAction: 'none', cursor: 'crosshair', overflow: 'hidden' }}>
        <canvas ref={canvasRef} width={560} height={160} style={{ display: 'block', width: '100%', height: 160 }}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
        {!has && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}><span style={{ color: '#94a3b8', fontSize: 13 }}>Signez ici pour valider le BAT</span></div>}
      </div>
      {has && <button onClick={clear} style={{ alignSelf: 'flex-end', fontSize: 11, color: '#dc2626', background: 'none', border: '1px solid #fecaca', borderRadius: 5, padding: '3px 10px', cursor: 'pointer' }}>Effacer</button>}
    </div>
  );
};

// ── Carte produit ────────────────────────────────────────────
const ProductCard = ({ product, selected, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      border: `2px solid ${selected ? '#e63946' : '#e2e8f0'}`,
      borderRadius: 12, background: '#fff', cursor: 'pointer',
      overflow: 'hidden', transition: 'all .15s',
      boxShadow: selected ? '0 0 0 3px rgba(230,57,70,.1)' : 'none',
    }}
  >
    {product.main_image ? (
      <img src={product.main_image} alt={product.product_name} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
    ) : (
      <div style={{ height: 180, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>👕</div>
    )}
    <div style={{ padding: '14px 16px' }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>{product.product_name || 'Produit'}</div>
      {product.reference && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Réf. {product.reference}</div>}
      {product.description && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{product.description}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#e63946' }}>
          {product.custom_price ? parseFloat(product.custom_price).toFixed(2) : parseFloat(product.base_price || 0).toFixed(2)} € HT
          {product.custom_price && <span style={{ fontSize: 10, marginLeft: 4, color: '#16a34a', fontWeight: 600 }}>Tarif spécial</span>}
        </div>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: selected ? '#e63946' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: selected ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 16 }}>
          {selected ? '✓' : '+'}
        </div>
      </div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════
//  PAGE PORTAIL CLIENT
// ═════════════════════════════════════════════════════════════
const ExtranetPortalPage = () => {
  const { token }    = useParams();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [selection,  setSelection]  = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [signature,  setSignature]  = useState(null);
  const [accepted,   setAccepted]   = useState(false);
  const [comment,    setComment]    = useState('');
  const [signing,    setSigning]    = useState(false);
  const { settings } = useSite();

  // Déterminer l'étape en fonction du statut serveur
  const getStep = useCallback((d) => {
    if (!d) return 'products';
    if (d.status === 'bat_signed') return 'done';
    // Étape 2 (BAT) : le client a sélectionné ET l'admin a envoyé le BAT (pending_bat)
    // OU le client a fait sa sélection (active) — il peut voir ses choix mais pas signer tant que c'est pas pending_bat
    if (d.status === 'pending_bat') return 'bat';
    if (d.status === 'active' && d.selection) return 'waiting'; // en attente du BAT par l'admin
    // Étape 1 : sélection
    return 'products';
  }, []);

  const loadData = useCallback(async () => {
    try {
      const res = await extranetAPI.portal(token);
      const d = res.data.data;
      setData(d);
      if (d.selection) setSelection(d.selection.selected_ids || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Espace introuvable ou expiré');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const step = getStep(data);

  const toggleProduct = (id) => {
    setSelection(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const saveSelection = async () => {
    if (!selection.length) { toast.error('Sélectionnez au moins un produit'); return; }
    setSaving(true);
    try {
      await extranetAPI.select(token, { selection: { selected_ids: selection } });
      toast.success('Sélection enregistrée');
      await loadData(); // Re-fetch pour obtenir le nouveau statut
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const signBat = async () => {
    if (!signature) { toast.error('Veuillez apposer votre signature'); return; }
    if (!accepted)  { toast.error('Vous devez accepter le BAT'); return; }
    setSigning(true);
    try {
      await extranetAPI.signBat(token, { signature_data: signature, accepted: true, comment });
      toast.success('BAT signé avec succès');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSigning(false);
    }
  };

  const goBackToSelection = async () => {
    try {
      await extranetAPI.clientStepBack(token);
      toast.success('Retour à la sélection');
      setSelection([]);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  // ── Rendu ─────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24 }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontWeight: 800, color: '#1e293b' }}>Espace introuvable</h2>
        <p style={{ color: '#64748b', marginTop: 8 }}>{error}</p>
      </div>
    </div>
  );

  const products = data?.products || [];
  const files    = data?.files || [];
  const stepNames = ['products','waiting','bat','done'];
  const stepIdx   = stepNames.indexOf(step);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ background: '#1e293b', padding: '20px 24px', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 8px rgba(0,0,0,.2)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: -0.5 }}>{settings.site_name}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Espace client — {data.title}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{data.client_name}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Valide jusqu'au {new Date(data.valid_until).toLocaleDateString('fr-FR')}</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }}>

        {/* Barre d'étapes */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 32, background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {[['Sélection', 0], ['En attente', 1], ['BAT & Signature', 2], ['Terminé', 3]].map(([l, i]) => {
            const isDone   = stepIdx > i;
            const isActive = stepIdx === i;
            return (
              <div key={i} style={{ flex: 1, padding: '14px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12,
                background: isActive ? '#e63946' : isDone ? '#f0fdf4' : '#fff',
                color: isActive ? '#fff' : isDone ? '#16a34a' : '#94a3b8',
                borderRight: i < 3 ? '1px solid #e2e8f0' : 'none' }}>
                {l}
              </div>
            );
          })}
        </div>

        {/* Description */}
        {data.description && step === 'products' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 24, border: '1px solid #e2e8f0', color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
            {data.description}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
           ÉTAPE 1 : Sélection des produits
           ═══════════════════════════════════════════════════ */}
        {step === 'products' && (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', marginBottom: 20 }}>
              Nos propositions pour votre projet
            </h2>

            {!products.length ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 12 }}>
                <p>Aucun produit proposé pour le moment.</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20, marginBottom: 28 }}>
                  {products.map(p => (
                    <ProductCard key={p.id} product={p}
                      selected={selection.includes(p.product_id || p.id)}
                      onToggle={() => toggleProduct(p.product_id || p.id)} />
                  ))}
                </div>

                <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 16 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>
                    <strong>{selection.length}</strong> produit(s) sélectionné(s)
                  </div>
                  <button onClick={saveSelection} disabled={saving || !selection.length}
                    style={{ padding: '12px 28px', background: selection.length ? '#e63946' : '#e2e8f0', color: selection.length ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 14, border: 'none', borderRadius: 8, cursor: selection.length ? 'pointer' : 'not-allowed' }}>
                    {saving ? 'Enregistrement...' : 'Envoyer ma sélection →'}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════
           EN ATTENTE du BAT par l'admin
           ═══════════════════════════════════════════════════ */}
        {step === 'waiting' && (
          <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>⏳</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>Sélection enregistrée !</h2>
            <p style={{ color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
              Votre sélection a bien été transmise à notre équipe.<br/>
              Nous préparons votre Bon à Tirer (BAT). Vous recevrez un email dès qu'il sera prêt à valider.
            </p>

            {/* Récap de la sélection */}
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', textAlign: 'left', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Votre sélection :</div>
              {products.filter(p => selection.includes(p.product_id || p.id)).map(p => (
                <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}>
                  {p.main_image && <img src={p.main_image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.product_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Qté : {p.quantity}</div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={goBackToSelection}
              style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
              ← Modifier ma sélection
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
           ÉTAPE 2 : Consultation BAT + Signature
           ═══════════════════════════════════════════════════ */}
        {step === 'bat' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: 0 }}>Bon à Tirer (BAT)</h2>
              <button onClick={goBackToSelection}
                style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
                ← Modifier ma sélection
              </button>
            </div>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
              Consultez les documents ci-dessous, puis signez pour valider la production.
            </p>

            {/* Documents / fichiers BAT */}
            {files.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Documents transmis par notre équipe</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {files.map(f => (
                    <a key={f.id} href={f.url} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', textDecoration: 'none', color: '#1e293b' }}>
                      <span style={{ fontSize: 22 }}>{f.mime_type?.includes('pdf') ? '📄' : '🖼'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#e63946' }}>{f.original_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Cliquez pour consulter · {(f.file_size / 1024).toFixed(0)} Ko</div>
                      </div>
                      <span style={{ fontSize: 18, color: '#94a3b8' }}>→</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Récap sélection */}
            {selection.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Votre sélection :</div>
                {products.filter(p => selection.includes(p.product_id || p.id)).map(p => (
                  <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}>
                    {p.main_image && <img src={p.main_image} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />}
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.product_name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{p.custom_price ? `${parseFloat(p.custom_price).toFixed(2)} €` : `${parseFloat(p.base_price || 0).toFixed(2)} €`} · Qté : {p.quantity}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Zone signature */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Signature</div>
              <SignatureCanvas onChange={setSignature} />
            </div>

            {/* Commentaire */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Commentaire (optionnel)</div>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                placeholder="Remarques éventuelles..." />
            </div>

            {/* Acceptation + bouton signer */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0' }}>
              <label style={{ display: 'flex', gap: 12, cursor: 'pointer', marginBottom: 20 }}>
                <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)}
                  style={{ marginTop: 2, width: 18, height: 18, accentColor: '#e63946', cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                  Je valide le Bon à Tirer pour le projet <strong>{data.title}</strong> et j'autorise la production avec les produits sélectionnés.
                </span>
              </label>

              <button onClick={signBat} disabled={signing || !signature || !accepted}
                style={{ width: '100%', padding: 16, fontSize: 16, fontWeight: 800,
                  background: signature && accepted ? '#e63946' : '#e2e8f0',
                  color: signature && accepted ? '#fff' : '#94a3b8',
                  border: 'none', borderRadius: 10,
                  cursor: signature && accepted ? 'pointer' : 'not-allowed' }}>
                {signing ? 'Signature en cours...' : 'Signer le BAT et lancer la production'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
                Signature horodatée et sécurisée (SHA-256)
              </p>
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════════════
           TERMINÉ
           ═══════════════════════════════════════════════════ */}
        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 72, marginBottom: 20 }}>🎉</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#16a34a', marginBottom: 8 }}>BAT signé avec succès !</h2>
            <p style={{ color: '#64748b', marginBottom: 24, lineHeight: 1.6 }}>
              Votre Bon à Tirer a été enregistré. Notre équipe de production va maintenant prendre en charge votre commande.
            </p>
            {data.bat?.proof_hash && (
              <div style={{ background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 10, padding: '14px 20px', display: 'inline-block', textAlign: 'left' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', marginBottom: 6 }}>Référence de preuve</div>
                <div style={{ fontSize: 11, color: '#166534', fontFamily: 'monospace' }}>{data.bat.proof_hash}</div>
              </div>
            )}
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 20 }}>Un email de confirmation vous a été envoyé.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtranetPortalPage;
